import { PopoverProps } from "@radix-ui/react-popover";
import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMutationObserver } from "@/hooks/use-mutation-observer";
import { cn } from "@/lib/utils";
import { SelectOption } from "./select";

export interface ComboboxProps extends PopoverProps {
  options: SelectOption[];
  selectedOption: SelectOption | null;
  onSelectOption: (option: SelectOption) => void;
  searchable?: boolean;
  peekable?: boolean;
}

export function Combobox({ options, selectedOption, onSelectOption, ...props }: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [peekedOption, setPeekedOption] = React.useState<SelectOption | null>(null);

  return (
    <div className="grid gap-2">
      <Popover open={open} onOpenChange={setOpen} {...props}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select an option"
            className="w-full justify-between"
          >
            {selectedOption ? selectedOption.name : "Select an option..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" className="w-[250px] p-0">
          <HoverCard>
            {props.peekable && (
              <HoverCardContent side="left" align="start" forceMount>
                <div className="grid gap-2">
                  <h4 className="font-medium leading-none">{peekedOption?.name}</h4>
                  <div className="text-sm text-muted-foreground">{peekedOption?.description}</div>
                </div>
              </HoverCardContent>
            )}
            <Command loop>
              <CommandList className="h-[var(--cmdk-list-height)] max-h-[300px]">
                {props.searchable && (
                  <>
                    <CommandInput placeholder="Search options..." />
                    <CommandEmpty>No options found.</CommandEmpty>
                  </>
                )}
                <HoverCardTrigger />
                {options.map((option) => (
                  <ComboboxItem
                    key={option.id}
                    option={option}
                    isSelected={selectedOption?.id === option.id}
                    onPeek={(option) => setPeekedOption(option)}
                    onSelect={() => {
                      onSelectOption(option);
                      setOpen(false);
                    }}
                  />
                ))}
              </CommandList>
            </Command>
          </HoverCard>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface ComboboxItemProps {
  option: SelectOption;
  isSelected: boolean;
  onSelect: () => void;
  onPeek: (option: SelectOption) => void;
}

function ComboboxItem({ option, isSelected, onSelect, onPeek }: ComboboxItemProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  useMutationObserver(ref, (mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        if (mutation.attributeName === "aria-selected") {
          onPeek(option);
        }
      }
    }
  });

  return (
    <CommandItem
      key={option.id}
      onSelect={onSelect}
      ref={ref}
      className="aria-selected:bg-primary aria-selected:text-primary-foreground dark:aria-selected:bg-brand-accent dark:aria-selected:text-brand-accent-foreground"
    >
      {option.name}
      <Check className={cn("ml-auto h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );
}
